import { useEffect, useRef, useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuChevronDown, LuChevronRight, LuSquarePen } from 'react-icons/lu';
import BrikAgente, { type EstadoBrik } from './BrikAgente';
import AssistenteMarkdown from './AssistenteMarkdown';
import type { EstadoFala } from './BrikFala';
import type { AberturaAssistente } from '../services/AssistenteService';
import './BrikFala.css';

/**
 * O Brik no topo da conversa da Home (07/10/2026, direção B de
 * `docs/designsystem/propostas/home-brik-conversa.html`). Quando a primeira
 * pergunta sai, o balão de abertura se recolhe nesta faixa: o Brik vivo, a
 * saudação e o resumo do dia numa linha. "Ver o resumo" mostra a fala inteira de
 * novo. A conversa rola por baixo, e o Brik nunca sai da tela: pensa enquanto a
 * resposta não vem e dá o pulinho quando ela chega.
 */
const BrikFaixa: FC<{
    saudacao: string;
    estado: EstadoFala;
    abertura?: AberturaAssistente | null;
    proximoPasso?: string | null;
    /** A resposta ainda não começou a aparecer: o Brik pensa. */
    pensando: boolean;
    /** A resposta está aparecendo (sendo escrita): o Brik fala, com a boca mexendo. */
    falando?: boolean;
    /** Quando chegou a última resposta (muda a cada uma): o Brik dá o pulinho. */
    respondeuEm?: number | null;
    /** Volta para a tela inicial, com o balão e as conversas recentes (07/10/2026). */
    onNovaConversa?: () => void;
    /** Enquanto a resposta não chega, não dá para trocar de conversa. */
    novaBloqueada?: boolean;
    celular?: boolean;
}> = ({ saudacao, estado, abertura, proximoPasso, pensando, falando = false, respondeuEm, onNovaConversa, novaBloqueada = false, celular = false }) => {
    const navigate = useNavigate();
    const [aberta, setAberta] = useState(false);

    /* O pulinho é da resposta nova, não da faixa aparecendo: a primeira
       mudança depois de montar já é uma resposta. */
    const [chegou, setChegou] = useState(false);
    const ultima = useRef(respondeuEm);
    useEffect(() => {
        if (!respondeuEm || respondeuEm === ultima.current) return;
        ultima.current = respondeuEm;
        setChegou(true);
        const t = setTimeout(() => setChegou(false), 1000);
        return () => clearTimeout(t);
    }, [respondeuEm]);

    const contaNova = estado === 'pronto' && !!abertura?.contaNova;
    const temResumo = estado === 'pronto' && !!abertura;

    let linha: string | null = null;
    if (estado === 'carregando') linha = 'Olhando o seu dia…';
    else if (contaNova) linha = 'Sua conta está começando';
    else if (temResumo && abertura) linha = abertura.resumo || abertura.fala.replace(/\*\*/g, '');

    const estadoBrik: EstadoBrik = pensando ? 'pensando' : falando ? 'falando' : chegou ? 'feito' : 'repouso';

    return (
        <div className={`hc-faixa${aberta ? ' is-aberta' : ''}`}>
            <BrikAgente tamanho="grande" px={celular ? 32 : 40} estado={estadoBrik} vivo />

            <div className="hc-faixa-txt">
                <h1 className="hc-faixa-tt">{saudacao}</h1>

                {!aberta && linha && <div className="hc-faixa-sub">{linha}</div>}

                {aberta && temResumo && abertura && (
                    <div className="hc-faixa-fala">
                        {contaNova ? (
                            <>
                                <span>
                                    Sua conta está começando, então ainda não tenho o que olhar.
                                    {proximoPasso && <> O próximo dos <b>Primeiros passos</b> é <b>{proximoPasso}</b>.</>}
                                </span>
                                {proximoPasso && (
                                    <button type="button" className="hc-link" onClick={() => navigate('/primeiros-passos')}>
                                        Continuar os Primeiros passos <LuChevronRight size={14} />
                                    </button>
                                )}
                            </>
                        ) : (
                            <AssistenteMarkdown texto={abertura.fala} />
                        )}
                    </div>
                )}
            </div>

            {temResumo && (
                <button
                    type="button"
                    className="hc-link hc-faixa-ver"
                    onClick={() => setAberta(a => !a)}
                    aria-expanded={aberta}
                >
                    {aberta ? 'Fechar' : 'Ver o resumo'}
                    <LuChevronDown size={13} className={aberta ? 'is-virado' : undefined} />
                </button>
            )}

            {/* O mesmo ícone de "nova conversa" do topo do painel. */}
            {onNovaConversa && (
                <button
                    type="button"
                    className="hc-faixa-nova"
                    onClick={onNovaConversa}
                    disabled={novaBloqueada}
                    aria-label="Nova conversa"
                    title="Nova conversa"
                >
                    <LuSquarePen size={15} />
                </button>
            )}
        </div>
    );
};

export default BrikFaixa;
